import {
  APIDashboardFilterParameter,
  APIFieldAttributes,
  APIFieldType,
  APILinkedDashboardFilterParameter,
  DashboardFilterValues,
  Operator,
} from '@ardoq/api-types';
import {
  DashboardFilter,
  DashboardFiltersLinkCountDict,
  DashboardFilterSuggestions,
  DashboardFiltersSuggestionsById,
  ListDashboardFilter,
  MinimalFieldRepresentation,
  UnlinkedDashboardFilter,
} from '../types';
import { UNSET_OR_NOTAPPLICABLE_VALUE_LABEL } from '../consts';
import { type I18nLocale, t } from '@ardoq/i18n';

const notSetOption = {
  value: null,
  label: UNSET_OR_NOTAPPLICABLE_VALUE_LABEL,
} as const;

/**
 * A list field's options are declared on the field itself, so they are read
 * from the fields store wherever it is loaded. The presentation viewer has no
 * fields store, and falls back to the suggestions endpoint, which serves the
 * same declared options under the presentation's permissions.
 */
const toListOptions = (
  field: MinimalFieldRepresentation | APIFieldAttributes | undefined,
  suggestions: DashboardFilterSuggestions | undefined
): Array<{ value: string; label: string }> => {
  if (
    field &&
    'defaultValue' in field &&
    typeof field.defaultValue === 'string' &&
    field.defaultValue !== ''
  ) {
    return field.defaultValue
      .split(',')
      .map(value => ({ value, label: value }));
  }
  if (suggestions?.kind === 'flat') {
    return suggestions.suggestions.map(({ value, label }) => ({
      value,
      label,
    }));
  }
  return [];
};

export const toDashboardFilter = (
  parameter: APILinkedDashboardFilterParameter,
  dashboardFiltersSuggestionsById: DashboardFiltersSuggestionsById,
  dashboardFiltersLinkCountDict: DashboardFiltersLinkCountDict | undefined,
  filterValues: DashboardFilterValues | undefined,
  fieldsByName: Record<string, MinimalFieldRepresentation | APIFieldAttributes>,
  i18nLocale: I18nLocale
): DashboardFilter => {
  const filterExpression = filterValues && filterValues[parameter.id];
  const type = parameter.type;
  const common = {
    field: {
      name: parameter.fieldApiName,
    },
    name: parameter.name,
    id: parameter.id,
    linkCounts: dashboardFiltersLinkCountDict?.[parameter.id] ?? {
      total: 0,
      warnings: 0,
    },
    operator:
      filterExpression && filterExpression.length
        ? filterExpression[0].operator
        : Operator.EQUAL,
  };

  if (type === APIFieldType.DATE_ONLY || type === APIFieldType.DATE_TIME) {
    const value = filterExpression?.[0]?.value ?? null;
    return {
      ...common,
      type,
      value: value as string | null,
    };
  } else if (type === APIFieldType.NUMBER) {
    const value = filterExpression?.[0]?.value ?? null;
    const field = fieldsByName[parameter.fieldApiName];
    return {
      ...common,
      type,
      value: value as number | null,
      numberFormatOptions:
        field &&
        'numberFormatOptions' in field &&
        field.type === APIFieldType.NUMBER &&
        field.numberFormatOptions
          ? field.numberFormatOptions
          : null,
    };
  }
  if (
    type === APIFieldType.LIST ||
    type === APIFieldType.SELECT_MULTIPLE_LIST
  ) {
    const options = toListOptions(
      fieldsByName[parameter.fieldApiName],
      dashboardFiltersSuggestionsById[parameter.id]
    );

    return {
      ...common,
      type,
      values: filterExpression ?? [],
      options: [notSetOption, ...options],
    } satisfies ListDashboardFilter;
  } else if (type === APIFieldType.CHECKBOX) {
    return {
      ...common,
      type,
      values: filterExpression ?? [],
      options: [
        {
          value: true,
          label: t(
            i18nLocale,
            'dashboard:filter.checkboxOption.checked',
            'Checked'
          ),
        },
        {
          value: false,
          label: t(
            i18nLocale,
            'dashboard:filter.checkboxOption.unchecked',
            'Unchecked'
          ),
        },
        notSetOption,
      ],
    };
  }
  const suggestionProps = dashboardFiltersSuggestionsById[parameter.id];
  return {
    ...common,
    type,
    values: filterExpression ?? [],
    ...(suggestionProps ? { suggestionProps } : {}),
  };
};

export const toUnlinkedDashboardFilter = (
  parameter: APIDashboardFilterParameter
): UnlinkedDashboardFilter => ({
  id: parameter.id,
  name: parameter.name,
  field: {
    name: undefined,
  },
  type: undefined,
  linkCounts: {
    warnings: 0,
    total: 0,
  },
});
